import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Layout, Eye, Settings, Trash2, ExternalLink, Copy,
  FileText, Globe, BarChart3, Palette, Type, Image as ImageIcon,
  List, CreditCard, Video, Quote, MessageSquare, X, Save, 
  GripVertical, ChevronUp, ChevronDown, Users, TrendingUp, DollarSign,
  Activity, ArrowUpRight, ArrowDownRight, Clock, Target, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PageBuilder from '../components/PageBuilder';
import TemplateMarketplace from '../components/TemplateMarketplace';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component Types for Drag & Drop Page Builder
const COMPONENT_TYPES = [
  { id: 'hero', label: 'Hero Section', icon: Layout, category: 'sections' },
  { id: 'text', label: 'Text Block', icon: Type, category: 'content' },
  { id: 'image', label: 'Image', icon: ImageIcon, category: 'media' },
  { id: 'video', label: 'Video Embed', icon: Video, category: 'media' },
  { id: 'form', label: 'Lead Capture Form', icon: Users, category: 'conversion' },
  { id: 'checkout', label: 'Payment Checkout', icon: CreditCard, category: 'conversion' },
  { id: 'features', label: 'Features Grid', icon: List, category: 'sections' },
  { id: 'testimonial', label: 'Testimonials', icon: Quote, category: 'social_proof' },
  { id: 'cta', label: 'Call to Action', icon: MessageSquare, category: 'conversion' },
  { id: 'countdown', label: 'Countdown Timer', icon: BarChart3, category: 'urgency' },
  { id: 'pricing', label: 'Pricing Table', icon: CreditCard, category: 'conversion' },
];

export default function Funnels() {
  // Get business content for page templates
  const { getPageTemplates, getBusinessTypeName, businessType } = useBusinessContent();
  
  const [funnels, setFunnels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('funnels');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [pageEditorData, setPageEditorData] = useState(null);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [showNewPageBuilder, setShowNewPageBuilder] = useState(false);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [funnelsRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/funnels`, { headers }),
        fetch(`${API_URL}/api/funnels/templates/list`, { headers })
      ]);

      if (funnelsRes.ok) {
        const data = await funnelsRes.json();
        setFunnels(data.funnels || []);
      }

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch funnels:', error);
      toast.error('Failed to load funnels');
    } finally {
      setLoading(false);
    }
  };

  const createFunnel = async (name, slug, templateId = null, marketplaceTemplate = null) => {
    try {
      const body = { name, slug };
      
      // If using marketplace template, include pages and theme
      if (marketplaceTemplate) {
        body.pages = marketplaceTemplate.pages;
        body.theme = marketplaceTemplate.theme;
      } else if (templateId) {
        body.template_id = templateId;
      }

      const res = await fetch(`${API_URL}/api/funnels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Funnel created successfully');
        fetchData();
        setShowCreateModal(false);
        setShowMarketplace(false);
        return data;
      } else {
        toast.error(data.detail || 'Failed to create funnel');
      }
    } catch (error) {
      toast.error('Failed to create funnel');
    }
    return null;
  };

  // Handle marketplace template selection
  const handleMarketplaceSelect = (template) => {
    // Create funnel from marketplace template
    const slugBase = template.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    createFunnel(template.name, slugBase, null, template);
  };

  const publishFunnel = async (funnelId) => {
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnelId}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Funnel published!');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to publish');
      }
    } catch (error) {
      toast.error('Failed to publish funnel');
    }
  };

  const unpublishFunnel = async (funnelId) => {
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnelId}/unpublish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Funnel unpublished');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  const deleteFunnel = async (funnelId) => {
    if (!window.confirm('Delete this funnel? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Funnel deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete funnel');
    }
  };

  const copyFunnelUrl = (funnel) => {
    const url = `${API_URL}/api/funnels/public/${funnel.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Funnel URL copied!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'draft': return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
      default: return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">{getBusinessTypeName()} Page Builder</h1>
        <p className="text-emerald-300/70">Create high-converting landing pages and lead capture funnels</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Layout className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{funnels.length}</p>
                <p className="text-sm text-emerald-300/70">Total Funnels</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <Globe className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {funnels.filter(f => f.status === 'published').length}
                </p>
                <p className="text-sm text-emerald-300/70">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {funnels.reduce((acc, f) => acc + (f.total_views || 0), 0)}
                </p>
                <p className="text-sm text-emerald-300/70">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {funnels.reduce((acc, f) => acc + (f.total_submissions || 0), 0)}
                </p>
                <p className="text-sm text-emerald-300/70">Leads Captured</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['funnels', 'templates'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab 
                ? 'bg-emerald-600 text-white' 
                : 'bg-[#0a3d2e] text-emerald-300/70 hover:bg-slate-700'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab === 'funnels' ? 'My Funnels' : 'Templates'}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'funnels' && (
          <motion.div
            key="funnels"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Your Funnels</h2>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowMarketplace(true)}
                  className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500"
                  data-testid="marketplace-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Marketplace
                </Button>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="create-funnel-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Funnel
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : funnels.length === 0 ? (
              <Card className="bg-[#052e22]/50 border-emerald-800/30 border-dashed">
                <CardContent className="p-12 text-center">
                  <Layout className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No funnels yet</h3>
                  <p className="text-emerald-300/70 mb-4">Create your first funnel to start capturing leads</p>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      onClick={() => setShowMarketplace(true)}
                      className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Browse Marketplace
                    </Button>
                    <Button 
                      onClick={() => setShowCreateModal(true)}
                      variant="outline"
                      className="border-emerald-800/30 text-slate-300"
                    >
                      Create from Scratch
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {funnels.map((funnel) => (
                  <Card 
                    key={funnel.id} 
                    className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-800/30 transition-all"
                    data-testid={`funnel-card-${funnel.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-white">{funnel.name}</h3>
                          <p className="text-sm text-emerald-300/70 mt-1">{funnel.description || 'No description'}</p>
                        </div>
                        <Badge className={`${getStatusColor(funnel.status)} border`}>
                          {funnel.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-emerald-300/70 mb-4">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{funnel.total_pages || 0} pages</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          <span>{funnel.total_views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <BarChart3 className="w-4 h-4" />
                          <span>{funnel.conversion_rate || 0}%</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {funnel.status === 'draft' ? (
                          <Button
                            size="sm"
                            onClick={() => publishFunnel(funnel.id)}
                            className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30"
                            data-testid={`publish-funnel-${funnel.id}`}
                          >
                            <Globe className="w-4 h-4 mr-1" /> Publish
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => unpublishFunnel(funnel.id)}
                            className="bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30"
                          >
                            Unpublish
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-300/70 hover:text-white"
                          onClick={() => setSelectedFunnel(funnel)}
                          data-testid={`edit-funnel-${funnel.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        
                        {funnel.status === 'published' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-300/70 hover:text-white"
                              onClick={() => window.open(`${API_URL}/api/funnels/public/${funnel.slug}`, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-300/70 hover:text-white"
                              onClick={() => copyFunnelUrl(funnel)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deleteFunnel(funnel.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'templates' && (
          <motion.div
            key="templates"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <h2 className="text-xl font-semibold text-white mb-6">Funnel Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-500/50 transition-all"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <Layout className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{template.name}</h3>
                        <Badge variant="outline" className="text-xs text-emerald-300/70 border-emerald-800/30">
                          {template.category}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-emerald-300/70 mb-4">{template.description}</p>
                    
                    <div className="flex items-center gap-2 text-sm text-emerald-300/70 mb-4">
                      <FileText className="w-4 h-4" />
                      <span>{template.pages_count} pages</span>
                    </div>

                    <Button
                      onClick={() => {
                        setShowCreateModal(true);
                      }}
                      className="w-full bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30"
                      data-testid={`use-template-${template.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Use Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Funnel Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateFunnelModal
            templates={templates}
            onClose={() => setShowCreateModal(false)}
            onCreate={createFunnel}
          />
        )}
      </AnimatePresence>

      {/* Funnel Editor Modal */}
      <AnimatePresence>
        {selectedFunnel && (
          <FunnelEditorModal
            funnel={selectedFunnel}
            token={token}
            onClose={() => setSelectedFunnel(null)}
            onRefresh={fetchData}
            onEditPage={(page) => setPageEditorData({ funnel: selectedFunnel, page })}
          />
        )}
      </AnimatePresence>

      {/* Page Editor (Drag & Drop) */}
      <AnimatePresence>
        {pageEditorData && (
          <PageEditor
            funnel={pageEditorData.funnel}
            page={pageEditorData.page}
            token={token}
            onClose={() => setPageEditorData(null)}
          />
        )}
      </AnimatePresence>

      {/* New Page Builder */}
      <AnimatePresence>
        {showNewPageBuilder && pageEditorData && (
          <PageBuilder
            funnel={pageEditorData.funnel}
            page={pageEditorData.page}
            token={token}
            onClose={() => {
              setShowNewPageBuilder(false);
              setPageEditorData(null);
            }}
            onSave={() => {
              fetchData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Template Marketplace Modal */}
      <AnimatePresence>
        {showMarketplace && (
          <TemplateMarketplace
            onSelectTemplate={handleMarketplaceSelect}
            onClose={() => setShowMarketplace(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Create Funnel Modal
function CreateFunnelModal({ templates, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [templateId, setTemplateId] = useState('');

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setSlug(value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a funnel name');
      return;
    }
    if (!slug.trim()) {
      toast.error('Please enter a URL slug');
      return;
    }
    onCreate(name, slug, templateId || null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-white mb-4">Create New Funnel</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Funnel Name</label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="e.g., Lead Capture Funnel"
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              data-testid="funnel-name-input"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">URL Slug</label>
            <div className="flex items-center">
              <span className="text-gray-500 text-sm mr-2">/f/</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="lead-capture"
                className="flex-1 px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid="funnel-slug-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Start from Template (optional)</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white focus:outline-none focus:border-emerald-500"
              data-testid="template-select"
            >
              <option value="">Blank Funnel</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-emerald-300/70">
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-funnel-submit">
              Create Funnel
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Funnel Editor Modal (with Page List)
function FunnelEditorModal({ funnel, token, onClose, onRefresh, onEditPage }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPage, setShowAddPage] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState('pages');

  useEffect(() => {
    fetchPages();
  }, [funnel.id]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnel.id}/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const addPage = async (name, slug, type) => {
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnel.id}/pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, slug, type, components: [] })
      });

      if (res.ok) {
        toast.success('Page created');
        fetchPages();
        onRefresh();
        setShowAddPage(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create page');
      }
    } catch (error) {
      toast.error('Failed to create page');
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnel.id}/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Page deleted');
        fetchPages();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-emerald-800/30 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white">{funnel.name}</h3>
            <p className="text-sm text-emerald-300/70">/{funnel.slug}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={funnel.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-emerald-300/70'}>
              {funnel.status}
            </Badge>
            <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-emerald-800/30">
          <button
            onClick={() => setActiveEditorTab('pages')}
            className={`px-6 py-3 font-medium transition-all flex items-center gap-2 ${
              activeEditorTab === 'pages' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-emerald-300/70 hover:text-white'
            }`}
            data-testid="editor-tab-pages"
          >
            <FileText className="w-4 h-4" /> Pages
          </button>
          <button
            onClick={() => setActiveEditorTab('analytics')}
            className={`px-6 py-3 font-medium transition-all flex items-center gap-2 ${
              activeEditorTab === 'analytics' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-emerald-300/70 hover:text-white'
            }`}
            data-testid="editor-tab-analytics"
          >
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {activeEditorTab === 'pages' && (
            <>
              {/* Theme Settings */}
              <div className="mb-6">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Theme
                </h4>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full border-2 border-white"
                      style={{ backgroundColor: funnel.theme?.primary_color || '#7c3aed' }}
                    />
                    <span className="text-sm text-emerald-300/70">Primary</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full border-2 border-white"
                      style={{ backgroundColor: funnel.theme?.background_color || '#0f172a' }}
                    />
                    <span className="text-sm text-emerald-300/70">Background</span>
                  </div>
                </div>
              </div>

              {/* Pages */}
              <div className="mb-4 flex items-center justify-between">
                <h4 className="font-medium text-white flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Pages ({pages.length})
                </h4>
                <Button 
                  size="sm"
                  onClick={() => setShowAddPage(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-page-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Page
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
                </div>
              ) : pages.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-emerald-800/30 rounded-lg">
                  <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-emerald-300/70">No pages yet. Add your first page to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pages.map((page, index) => (
                    <div 
                      key={page.id}
                      className="bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg p-4 flex items-center gap-4"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-white">{page.name}</p>
                          <Badge className="text-xs bg-slate-700 text-slate-300">{page.type}</Badge>
                        </div>
                        <p className="text-sm text-gray-500">/{funnel.slug}/{page.slug}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-emerald-300/70 mr-2">
                          {page.components?.length || 0} components
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:text-emerald-300"
                          onClick={() => onEditPage(page)}
                          data-testid={`edit-page-${page.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        {funnel.status === 'published' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-300/70 hover:text-white"
                            onClick={() => window.open(`${API_URL}/api/funnels/public/${funnel.slug}/${page.slug}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deletePage(page.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Page Form */}
              {showAddPage && (
                <AddPageForm
                  onCancel={() => setShowAddPage(false)}
                  onSubmit={addPage}
                />
              )}
            </>
          )}

          {activeEditorTab === 'analytics' && (
            <FunnelAnalyticsDashboard funnelId={funnel.id} token={token} pages={pages} />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Add Page Form
function AddPageForm({ onCancel, onSubmit }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState('landing');

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setSlug(value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    onSubmit(name, slug, type);
  };

  return (
    <div className="mt-4 p-4 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg">
      <h5 className="font-medium text-white mb-4">Add New Page</h5>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm text-emerald-300/70 mb-1">Page Name</label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="e.g., Landing Page"
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="page-name-input"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-1">URL Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="landing-page"
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="page-slug-input"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-1">Page Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="page-type-select"
            >
              <option value="landing">Landing Page</option>
              <option value="optin">Opt-in Page</option>
              <option value="thank_you">Thank You Page</option>
              <option value="sales">Sales Page</option>
              <option value="webinar">Webinar Page</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} className="text-emerald-300/70">
            Cancel
          </Button>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-page-btn">
            Add Page
          </Button>
        </div>
      </form>
    </div>
  );
}

// Funnel Analytics Dashboard
function FunnelAnalyticsDashboard({ funnelId, token, pages }) {
  const [analytics, setAnalytics] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [pageStats, setPageStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [funnelId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [summaryRes, dailyRes, pageRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/funnel-analytics/${funnelId}/summary?period=${period}`, { headers }),
        fetch(`${API_URL}/api/funnel-analytics/${funnelId}/daily?period=${period}`, { headers }),
        fetch(`${API_URL}/api/funnel-analytics/${funnelId}/pages?period=${period}`, { headers }),
        fetch(`${API_URL}/api/funnel-analytics/${funnelId}/recent-activity?limit=10`, { headers })
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setAnalytics(data);
      }

      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailyStats(data.daily_stats || []);
      }

      if (pageRes.ok) {
        const data = await pageRes.json();
        setPageStats(data.page_stats || []);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        setRecentActivity(data.activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${(amount / 100).toLocaleString('en-IN')}`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getPageName = (pageId) => {
    const page = pages.find(p => p.id === pageId);
    return page?.name || pageId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="funnel-analytics-dashboard">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-400" /> Performance Analytics
        </h4>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          data-testid="analytics-period-select"
        >
          <option value="today">Today</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Page Views */}
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-300/70 mb-1">Page Views</p>
                <p className="text-2xl font-bold text-white" data-testid="metric-pageviews">
                  {formatNumber(analytics?.page_views || 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatNumber(analytics?.unique_visitors || 0)} unique visitors
                </p>
              </div>
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Submissions */}
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-300/70 mb-1">Form Submissions</p>
                <p className="text-2xl font-bold text-white" data-testid="metric-submissions">
                  {formatNumber(analytics?.form_submissions || 0)}
                </p>
                <p className="text-xs mt-1 flex items-center gap-1">
                  <span className={`${analytics?.form_conversion_rate > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {analytics?.form_conversion_rate?.toFixed(1) || 0}% conv. rate
                  </span>
                </p>
              </div>
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checkout Conversions */}
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-300/70 mb-1">Successful Payments</p>
                <p className="text-2xl font-bold text-white" data-testid="metric-payments">
                  {formatNumber(analytics?.checkout_completed || 0)}
                </p>
                <p className="text-xs mt-1">
                  <span className={`${analytics?.checkout_conversion_rate > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {analytics?.checkout_conversion_rate?.toFixed(1) || 0}% checkout rate
                  </span>
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CreditCard className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card className="bg-gradient-to-br from-emerald-600/20 to-teal-600/20 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-300 mb-1">Total Revenue</p>
                <p className="text-2xl font-bold text-white" data-testid="metric-revenue">
                  {formatCurrency(analytics?.total_revenue || 0)}
                </p>
                <p className="text-xs text-emerald-300/70 mt-1">
                  Avg. {formatCurrency(analytics?.average_order_value || 0)} per order
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <h5 className="font-medium text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" /> Daily Trend
            </h5>
            {dailyStats.length > 0 ? (
              <div className="h-40">
                <div className="flex h-full items-end gap-1">
                  {dailyStats.slice(-14).map((day, i) => {
                    const maxViews = Math.max(...dailyStats.map(d => d.page_views || 0), 1);
                    const height = ((day.page_views || 0) / maxViews) * 100;
                    return (
                      <div 
                        key={day.date || i}
                        className="flex-1 group relative"
                      >
                        <div 
                          className="bg-emerald-500/60 hover:bg-emerald-500 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#052e22] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                          {day.date}: {day.page_views || 0} views
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{dailyStats[dailyStats.length - 14]?.date || ''}</span>
                  <span>{dailyStats[dailyStats.length - 1]?.date || 'Today'}</span>
                </div>
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-500">
                No data yet. Start driving traffic to your funnel!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Performance */}
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <h5 className="font-medium text-white mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" /> Page Performance
            </h5>
            {pageStats.length > 0 ? (
              <div className="space-y-3 max-h-40 overflow-y-auto">
                {pageStats.map((stat) => (
                  <div key={stat.page_id} className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{getPageName(stat.page_id)}</p>
                      <div className="flex gap-3 text-xs text-gray-500">
                        <span>{stat.page_views || 0} views</span>
                        <span>{stat.form_submissions || 0} leads</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-400">
                        {stat.form_conversion_rate?.toFixed(1) || 0}%
                      </p>
                      <p className="text-xs text-gray-500">conv.</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-500">
                No page data available yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
        <CardContent className="p-4">
          <h5 className="font-medium text-white mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" /> Recent Activity
          </h5>
          {recentActivity.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.map((activity, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-emerald-800/30/50 last:border-0">
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'payment' 
                      ? 'bg-emerald-500/20' 
                      : 'bg-blue-500/20'
                  }`}>
                    {activity.type === 'payment' ? (
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Users className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white">{activity.description}</p>
                    <p className="text-xs text-gray-500">
                      {activity.page_slug && `on /${activity.page_slug}`}
                    </p>
                  </div>
                  <div className="text-right">
                    {activity.type === 'payment' && (
                      <p className="text-sm font-medium text-emerald-400">
                        +₹{activity.amount}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {activity.timestamp && new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No activity yet. Publish your funnel to start tracking!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversion Funnel Visualization */}
      {(analytics?.page_views > 0 || analytics?.form_submissions > 0 || analytics?.checkout_completed > 0) && (
        <Card className="bg-[#0a3d2e]/50 border-emerald-800/30">
          <CardContent className="p-4">
            <h5 className="font-medium text-white mb-4 flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-400" /> Conversion Funnel
            </h5>
            <div className="flex items-center gap-2">
              {/* Page Views */}
              <div className="flex-1 text-center">
                <div className="h-20 bg-emerald-500/30 rounded-lg flex items-center justify-center relative">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{formatNumber(analytics?.page_views || 0)}</p>
                    <p className="text-xs text-emerald-300">Visitors</p>
                  </div>
                </div>
              </div>
              
              <ArrowUpRight className="w-5 h-5 text-gray-500 rotate-45" />
              
              {/* Form Submissions */}
              <div className="flex-1 text-center">
                <div 
                  className="bg-amber-500/30 rounded-lg flex items-center justify-center relative"
                  style={{ height: `${Math.max(20, (analytics?.form_submissions / Math.max(analytics?.page_views, 1)) * 80)}px` }}
                >
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{formatNumber(analytics?.form_submissions || 0)}</p>
                    <p className="text-xs text-amber-300">Leads</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{analytics?.form_conversion_rate?.toFixed(1) || 0}%</p>
              </div>
              
              <ArrowUpRight className="w-5 h-5 text-gray-500 rotate-45" />
              
              {/* Payments */}
              <div className="flex-1 text-center">
                <div 
                  className="bg-emerald-500/30 rounded-lg flex items-center justify-center relative"
                  style={{ height: `${Math.max(20, (analytics?.checkout_completed / Math.max(analytics?.page_views, 1)) * 80)}px` }}
                >
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{formatNumber(analytics?.checkout_completed || 0)}</p>
                    <p className="text-xs text-emerald-300">Paid</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{((analytics?.checkout_completed / Math.max(analytics?.page_views, 1)) * 100).toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Page Editor - Drag & Drop Component Builder
function PageEditor({ funnel, page, token, onClose }) {
  const [components, setComponents] = useState(page.components || []);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draggedType, setDraggedType] = useState(null);

  const handleDragStart = (e, type) => {
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, index = null) => {
    e.preventDefault();
    if (!draggedType) return;

    const newComponent = createDefaultComponent(draggedType);
    
    if (index !== null) {
      const newComponents = [...components];
      newComponents.splice(index, 0, newComponent);
      setComponents(newComponents.map((c, i) => ({ ...c, order: i })));
    } else {
      setComponents([...components, { ...newComponent, order: components.length }]);
    }
    
    setDraggedType(null);
    setSelectedComponent(newComponent);
  };

  const moveComponent = (fromIndex, toIndex) => {
    const newComponents = [...components];
    const [movedComponent] = newComponents.splice(fromIndex, 1);
    newComponents.splice(toIndex, 0, movedComponent);
    setComponents(newComponents.map((c, i) => ({ ...c, order: i })));
  };

  const updateComponent = (componentId, updates) => {
    setComponents(components.map(c => 
      c.id === componentId ? { ...c, ...updates } : c
    ));
    if (selectedComponent?.id === componentId) {
      setSelectedComponent({ ...selectedComponent, ...updates });
    }
  };

  const deleteComponent = (componentId) => {
    setComponents(components.filter(c => c.id !== componentId));
    if (selectedComponent?.id === componentId) {
      setSelectedComponent(null);
    }
  };

  const saveComponents = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/funnels/${funnel.id}/pages/${page.id}/components`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(components)
      });

      if (res.ok) {
        toast.success('Page saved!');
      } else {
        toast.error('Failed to save page');
      }
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-950 z-[60]"
    >
      {/* Header */}
      <div className="h-14 border-b border-emerald-800/30 flex items-center justify-between px-4 bg-[#052e22]/50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="font-semibold text-white">{page.name}</h2>
            <p className="text-xs text-emerald-300/70">/{funnel.slug}/{page.slug}</p>
          </div>
        </div>
        <Button 
          onClick={saveComponents}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-page-components-btn"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Page
        </Button>
      </div>

      <div className="flex h-[calc(100vh-56px)]">
        {/* Component Palette */}
        <div className="w-64 border-r border-emerald-800/30 bg-[#052e22]/30 p-4 overflow-y-auto">
          <h3 className="text-sm font-semibold text-slate-300 mb-4">Drag Components</h3>
          
          {['sections', 'content', 'conversion', 'media', 'social_proof', 'urgency'].map(category => {
            const categoryComponents = COMPONENT_TYPES.filter(c => c.category === category);
            if (categoryComponents.length === 0) return null;
            
            return (
              <div key={category} className="mb-4">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">
                  {category.replace('_', ' ')}
                </p>
                <div className="space-y-1">
                  {categoryComponents.map(comp => {
                    const Icon = comp.icon;
                    return (
                      <div
                        key={comp.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, comp.id)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#0a3d2e]/50 hover:bg-emerald-500/20 border border-emerald-800/30/50 hover:border-emerald-500/50 cursor-grab active:cursor-grabbing text-sm text-slate-300 transition-all"
                      >
                        <GripVertical className="w-3 h-3 text-gray-500" />
                        <Icon className="w-4 h-4 text-emerald-400" />
                        <span>{comp.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Canvas */}
        <div 
          className="flex-1 overflow-y-auto bg-slate-950 p-8"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e)}
        >
          <div className="max-w-3xl mx-auto">
            {components.length === 0 ? (
              <div 
                className="border-2 border-dashed border-emerald-800/30 rounded-xl p-12 text-center"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 0)}
              >
                <Layout className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-emerald-300/70 mb-2">Drag components here to build your page</p>
                <p className="text-sm text-gray-500">Start with a Hero section or a Form</p>
              </div>
            ) : (
              <div className="space-y-4">
                {components.map((component, index) => (
                  <ComponentPreview
                    key={component.id}
                    component={component}
                    index={index}
                    totalCount={components.length}
                    isSelected={selectedComponent?.id === component.id}
                    onSelect={() => setSelectedComponent(component)}
                    onDelete={() => deleteComponent(component.id)}
                    onMoveUp={() => index > 0 && moveComponent(index, index - 1)}
                    onMoveDown={() => index < components.length - 1 && moveComponent(index, index + 1)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    theme={funnel.theme}
                  />
                ))}
                <div
                  className="border-2 border-dashed border-emerald-800/30/50 rounded-lg p-4 text-center text-gray-500 text-sm"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, components.length)}
                >
                  Drop here to add at the end
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        {selectedComponent && (
          <div className="w-80 border-l border-emerald-800/30 bg-[#052e22]/30 p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Component Settings</h3>
              <Button
                size="sm"
                variant="ghost"
                className="text-emerald-300/70"
                onClick={() => setSelectedComponent(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <ComponentEditor
              component={selectedComponent}
              onUpdate={(updates) => updateComponent(selectedComponent.id, updates)}
              theme={funnel.theme}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Component Preview (in Canvas)
function ComponentPreview({ component, index, totalCount, isSelected, onSelect, onDelete, onMoveUp, onMoveDown, onDragOver, onDrop, theme }) {
  const Icon = COMPONENT_TYPES.find(c => c.id === component.type)?.icon || Layout;
  
  return (
    <div
      className={`relative group rounded-xl border-2 transition-all ${
        isSelected 
          ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
          : 'border-emerald-800/30 hover:border-slate-600'
      }`}
      onClick={onSelect}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Controls */}
      <div className="absolute -top-3 left-4 flex items-center gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <Badge className="bg-emerald-500/90 text-white text-xs py-0.5">
          <Icon className="w-3 h-3 mr-1" />
          {COMPONENT_TYPES.find(c => c.id === component.type)?.label || component.type}
        </Badge>
      </div>
      
      <div className="absolute -top-3 right-4 flex items-center gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        {index > 0 && (
          <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="p-1 bg-slate-700 rounded hover:bg-slate-600">
            <ChevronUp className="w-3 h-3 text-white" />
          </button>
        )}
        {index < totalCount - 1 && (
          <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="p-1 bg-slate-700 rounded hover:bg-slate-600">
            <ChevronDown className="w-3 h-3 text-white" />
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 bg-red-500/80 rounded hover:bg-red-500">
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      </div>

      {/* Preview Content */}
      <div className="p-6" style={{ backgroundColor: theme?.background_color || '#0f172a' }}>
        {renderComponentPreview(component, theme)}
      </div>
    </div>
  );
}

// Component Editor (Properties Panel)
function ComponentEditor({ component, onUpdate, theme }) {
  const props = component.props || {};

  const updateProp = (key, value) => {
    onUpdate({ props: { ...props, [key]: value } });
  };

  return (
    <div className="space-y-4">
      {/* Component-specific editors */}
      {component.type === 'hero' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Headline</label>
            <input
              type="text"
              value={props.headline || ''}
              onChange={(e) => updateProp('headline', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Your main headline"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subheadline</label>
            <textarea
              value={props.subheadline || ''}
              onChange={(e) => updateProp('subheadline', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
              placeholder="Supporting text"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
            <input
              type="text"
              value={props.cta_text || ''}
              onChange={(e) => updateProp('cta_text', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Get Started"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Badge Text (optional)</label>
            <input
              type="text"
              value={props.badge_text || ''}
              onChange={(e) => updateProp('badge_text', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="LIMITED OFFER"
            />
          </div>
        </>
      )}

      {component.type === 'text' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Heading (optional)</label>
            <input
              type="text"
              value={props.heading || ''}
              onChange={(e) => updateProp('heading', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Content</label>
            <textarea
              value={props.content || ''}
              onChange={(e) => updateProp('content', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
            />
          </div>
        </>
      )}

      {component.type === 'form' && (
        <FormFieldsEditor
          fields={props.fields || []}
          onChange={(fields) => updateProp('fields', fields)}
          title={props.title || ''}
          onTitleChange={(title) => updateProp('title', title)}
          submitText={props.submit_text || ''}
          onSubmitTextChange={(text) => updateProp('submit_text', text)}
          redirectUrl={props.redirect_url || ''}
          onRedirectUrlChange={(url) => updateProp('redirect_url', url)}
        />
      )}

      {component.type === 'features' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Section Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Why Choose Us?"
            />
          </div>
          <FeaturesEditor
            features={props.features || []}
            onChange={(features) => updateProp('features', features)}
          />
        </>
      )}

      {component.type === 'testimonial' && (
        <TestimonialsEditor
          testimonials={props.testimonials || []}
          onChange={(testimonials) => updateProp('testimonials', testimonials)}
        />
      )}

      {component.type === 'cta' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Headline</label>
            <input
              type="text"
              value={props.headline || ''}
              onChange={(e) => updateProp('headline', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subheadline (optional)</label>
            <textarea
              value={props.subheadline || ''}
              onChange={(e) => updateProp('subheadline', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
            <input
              type="text"
              value={props.button_text || ''}
              onChange={(e) => updateProp('button_text', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Button Action</label>
            <select
              value={props.action_type || 'link'}
              onChange={(e) => updateProp('action_type', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            >
              <option value="link">Navigate to URL</option>
              <option value="scroll">Scroll to Section</option>
              <option value="checkout">Open Checkout</option>
            </select>
          </div>
          {(props.action_type === 'link' || !props.action_type) && (
            <div>
              <label className="block text-sm text-emerald-300/70 mb-2">URL</label>
              <input
                type="text"
                value={props.button_url || ''}
                onChange={(e) => updateProp('button_url', e.target.value)}
                className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
                placeholder="https://... or /page-slug"
              />
            </div>
          )}
          {props.action_type === 'scroll' && (
            <div>
              <label className="block text-sm text-emerald-300/70 mb-2">Scroll Target</label>
              <select
                value={props.scroll_target || '#form'}
                onChange={(e) => updateProp('scroll_target', e.target.value)}
                className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              >
                <option value="#form">Form Section</option>
                <option value="#checkout">Checkout Section</option>
                <option value="#features">Features Section</option>
                <option value="#pricing">Pricing Section</option>
              </select>
            </div>
          )}
          {props.action_type === 'checkout' && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-sm text-emerald-400">
                Button will scroll to the first checkout component on this page.
              </p>
            </div>
          )}
        </>
      )}

      {component.type === 'countdown' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Title</label>
            <input
              type="text"
              value={props.title || ''}
              onChange={(e) => updateProp('title', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="Offer Ends In"
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Target Date</label>
            <input
              type="datetime-local"
              value={props.target_date ? props.target_date.slice(0, 16) : ''}
              onChange={(e) => updateProp('target_date', e.target.value + ':00Z')}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'image' && (
        <>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Image URL</label>
            <input
              type="text"
              value={props.url || ''}
              onChange={(e) => updateProp('url', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Alt Text</label>
            <input
              type="text"
              value={props.alt || ''}
              onChange={(e) => updateProp('alt', e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            />
          </div>
        </>
      )}

      {component.type === 'video' && (
        <div>
          <label className="block text-sm text-emerald-300/70 mb-2">YouTube/Vimeo URL</label>
          <input
            type="text"
            value={props.url || ''}
            onChange={(e) => updateProp('url', e.target.value)}
            className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            placeholder="https://youtube.com/watch?v=..."
          />
        </div>
      )}

      {component.type === 'checkout' && (
        <CheckoutEditor
          props={props}
          onUpdate={updateProp}
        />
      )}

      {component.type === 'pricing' && (
        <PricingEditor
          plans={props.plans || []}
          onChange={(plans) => updateProp('plans', plans)}
        />
      )}
    </div>
  );
}

// Form Fields Editor
function FormFieldsEditor({ fields, onChange, title, onTitleChange, submitText, onSubmitTextChange, redirectUrl, onRedirectUrlChange }) {
  const addField = () => {
    onChange([...fields, { id: `field-${Date.now()}`, type: 'text', label: 'New Field', placeholder: '', required: false }]);
  };

  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange(newFields);
  };

  const removeField = (index) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  return (
    <>
      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Form Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="Get Started Free"
        />
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-emerald-300/70">Form Fields</label>
          <Button size="sm" variant="ghost" onClick={addField} className="text-emerald-400 h-6 px-2">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="p-2 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(index, { label: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                  placeholder="Field label"
                />
                <select
                  value={field.type}
                  onChange={(e) => updateField(index, { type: e.target.value })}
                  className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="textarea">Textarea</option>
                  <option value="select">Dropdown</option>
                </select>
                <button onClick={() => removeField(index)} className="p-1 text-red-400 hover:text-red-300">
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => updateField(index, { placeholder: e.target.value })}
                  className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                  placeholder="Placeholder text"
                />
                <label className="flex items-center gap-1 text-xs text-emerald-300/70">
                  <input
                    type="checkbox"
                    checked={field.required || false}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                    className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600"
                  />
                  Required
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Submit Button Text</label>
        <input
          type="text"
          value={submitText}
          onChange={(e) => onSubmitTextChange(e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="Submit"
        />
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Redirect URL (after submit)</label>
        <input
          type="text"
          value={redirectUrl}
          onChange={(e) => onRedirectUrlChange(e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="/thank-you"
        />
      </div>

      {/* CRM Integration Notice */}
      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mt-4">
        <div className="flex items-start gap-2">
          <Users className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-400 font-medium">CRM Integration Active</p>
            <p className="text-xs text-emerald-300/70 mt-1">
              Form submissions automatically create leads in your CRM and can trigger automation workflows.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// Features Editor
function FeaturesEditor({ features, onChange }) {
  const addFeature = () => {
    onChange([...features, { icon: 'star', title: 'New Feature', description: 'Feature description' }]);
  };

  const updateFeature = (index, updates) => {
    const newFeatures = [...features];
    newFeatures[index] = { ...newFeatures[index], ...updates };
    onChange(newFeatures);
  };

  const removeFeature = (index) => {
    onChange(features.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-emerald-300/70">Features</label>
        <Button size="sm" variant="ghost" onClick={addFeature} className="text-emerald-400 h-6 px-2">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {features.map((feature, index) => (
          <div key={index} className="p-2 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={feature.title}
                onChange={(e) => updateFeature(index, { title: e.target.value })}
                className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                placeholder="Feature title"
              />
              <button onClick={() => removeFeature(index)} className="p-1 text-red-400 hover:text-red-300">
                <X className="w-3 h-3" />
              </button>
            </div>
            <textarea
              value={feature.description}
              onChange={(e) => updateFeature(index, { description: e.target.value })}
              rows={2}
              className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs resize-none"
              placeholder="Feature description"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Testimonials Editor
function TestimonialsEditor({ testimonials, onChange }) {
  const addTestimonial = () => {
    onChange([...testimonials, { name: 'Customer Name', company: 'Company', text: 'Testimonial text...' }]);
  };

  const updateTestimonial = (index, updates) => {
    const newTestimonials = [...testimonials];
    newTestimonials[index] = { ...newTestimonials[index], ...updates };
    onChange(newTestimonials);
  };

  const removeTestimonial = (index) => {
    onChange(testimonials.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-emerald-300/70">Testimonials</label>
        <Button size="sm" variant="ghost" onClick={addTestimonial} className="text-emerald-400 h-6 px-2">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {testimonials.map((testimonial, index) => (
          <div key={index} className="p-2 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={testimonial.name}
                onChange={(e) => updateTestimonial(index, { name: e.target.value })}
                className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                placeholder="Name"
              />
              <input
                type="text"
                value={testimonial.company}
                onChange={(e) => updateTestimonial(index, { company: e.target.value })}
                className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                placeholder="Company"
              />
              <button onClick={() => removeTestimonial(index)} className="p-1 text-red-400 hover:text-red-300">
                <X className="w-3 h-3" />
              </button>
            </div>
            <textarea
              value={testimonial.text}
              onChange={(e) => updateTestimonial(index, { text: e.target.value })}
              rows={2}
              className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs resize-none"
              placeholder="Testimonial text"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Checkout Editor
function CheckoutEditor({ props, onUpdate }) {
  const addFeature = () => {
    const features = props.features || [];
    onUpdate('features', [...features, 'New Feature']);
  };

  const updateFeature = (index, value) => {
    const features = [...(props.features || [])];
    features[index] = value;
    onUpdate('features', features);
  };

  const removeFeature = (index) => {
    const features = (props.features || []).filter((_, i) => i !== index);
    onUpdate('features', features);
  };

  return (
    <>
      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Product Name</label>
        <input
          type="text"
          value={props.product_name || ''}
          onChange={(e) => onUpdate('product_name', e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="Your Product"
        />
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Product Description</label>
        <textarea
          value={props.product_description || ''}
          onChange={(e) => onUpdate('product_description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm resize-none"
          placeholder="Brief description..."
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-sm text-emerald-300/70 mb-2">Price (in paise)</label>
          <input
            type="number"
            value={props.price || 0}
            onChange={(e) => onUpdate('price', parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
            min="100"
          />
          <p className="text-xs text-gray-500 mt-1">= ₹{((props.price || 0) / 100).toFixed(2)}</p>
        </div>
        <div>
          <label className="block text-sm text-emerald-300/70 mb-2">Currency</label>
          <select
            value={props.currency || 'INR'}
            onChange={(e) => onUpdate('currency', e.target.value)}
            className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          >
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Button Text</label>
        <input
          type="text"
          value={props.button_text || ''}
          onChange={(e) => onUpdate('button_text', e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="Buy Now"
        />
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Success Redirect URL</label>
        <input
          type="text"
          value={props.success_redirect || ''}
          onChange={(e) => onUpdate('success_redirect', e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="/thank-you"
        />
      </div>

      <div>
        <label className="block text-sm text-emerald-300/70 mb-2">Guarantee Text</label>
        <input
          type="text"
          value={props.guarantee_text || ''}
          onChange={(e) => onUpdate('guarantee_text', e.target.value)}
          className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white text-sm"
          placeholder="30-day money-back guarantee"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-emerald-300/70">Product Features</label>
          <Button size="sm" variant="ghost" onClick={addFeature} className="text-emerald-400 h-6 px-2">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(props.features || []).map((feature, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={feature}
                onChange={(e) => updateFeature(index, e.target.value)}
                className="flex-1 px-2 py-1 bg-[#0a3d2e] border border-emerald-800/30 rounded text-white text-xs"
                placeholder="Feature..."
              />
              <button onClick={() => removeFeature(index)} className="p-1 text-red-400 hover:text-red-300">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="show_testimonial"
          checked={props.show_testimonial || false}
          onChange={(e) => onUpdate('show_testimonial', e.target.checked)}
          className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600"
        />
        <label htmlFor="show_testimonial" className="text-sm text-emerald-300/70">Show Testimonial</label>
      </div>

      {props.show_testimonial && (
        <div className="p-3 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30 space-y-2">
          <input
            type="text"
            value={props.testimonial?.name || ''}
            onChange={(e) => onUpdate('testimonial', { ...props.testimonial, name: e.target.value })}
            className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
            placeholder="Customer Name"
          />
          <textarea
            value={props.testimonial?.text || ''}
            onChange={(e) => onUpdate('testimonial', { ...props.testimonial, text: e.target.value })}
            rows={2}
            className="w-full px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs resize-none"
            placeholder="Testimonial text..."
          />
        </div>
      )}
    </>
  );
}

// Pricing Editor
function PricingEditor({ plans, onChange }) {
  const addPlan = () => {
    onChange([...plans, { name: 'New Plan', price: 19, features: ['Feature 1'], highlighted: false }]);
  };

  const updatePlan = (index, updates) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], ...updates };
    onChange(newPlans);
  };

  const removePlan = (index) => {
    onChange(plans.filter((_, i) => i !== index));
  };

  const addPlanFeature = (planIndex) => {
    const newPlans = [...plans];
    newPlans[planIndex].features = [...(newPlans[planIndex].features || []), 'New Feature'];
    onChange(newPlans);
  };

  const updatePlanFeature = (planIndex, featureIndex, value) => {
    const newPlans = [...plans];
    newPlans[planIndex].features[featureIndex] = value;
    onChange(newPlans);
  };

  const removePlanFeature = (planIndex, featureIndex) => {
    const newPlans = [...plans];
    newPlans[planIndex].features = newPlans[planIndex].features.filter((_, i) => i !== featureIndex);
    onChange(newPlans);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-emerald-300/70">Pricing Plans</label>
        <Button size="sm" variant="ghost" onClick={addPlan} className="text-emerald-400 h-6 px-2">
          <Plus className="w-3 h-3 mr-1" /> Add Plan
        </Button>
      </div>
      <div className="space-y-3">
        {plans.map((plan, index) => (
          <div key={index} className="p-3 bg-[#0a3d2e]/50 rounded-lg border border-emerald-800/30">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={plan.name}
                onChange={(e) => updatePlan(index, { name: e.target.value })}
                className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                placeholder="Plan Name"
              />
              <input
                type="number"
                value={plan.price}
                onChange={(e) => updatePlan(index, { price: parseInt(e.target.value) || 0 })}
                className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                placeholder="Price"
              />
              <button onClick={() => removePlan(index)} className="p-1 text-red-400 hover:text-red-300">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={plan.highlighted || false}
                onChange={(e) => updatePlan(index, { highlighted: e.target.checked })}
                className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600"
              />
              <span className="text-xs text-emerald-300/70">Highlight this plan</span>
            </div>
            <div className="space-y-1">
              {(plan.features || []).map((feature, fIndex) => (
                <div key={fIndex} className="flex items-center gap-1">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updatePlanFeature(index, fIndex, e.target.value)}
                    className="flex-1 px-2 py-0.5 bg-slate-700 border border-slate-600 rounded text-white text-xs"
                  />
                  <button onClick={() => removePlanFeature(index, fIndex)} className="p-0.5 text-red-400 hover:text-red-300">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => addPlanFeature(index)} 
                className="text-xs text-emerald-400 hover:text-emerald-300"
              >
                + Add Feature
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper functions
function createDefaultComponent(type) {
  const defaults = {
    hero: {
      headline: 'Your Amazing Headline Here',
      subheadline: 'A compelling subheadline that explains your value proposition',
      cta_text: 'Get Started',
      badge_text: ''
    },
    text: { heading: '', content: 'Your content goes here...' },
    image: { url: '', alt: '' },
    video: { url: '' },
    form: {
      title: 'Get Started Free',
      fields: [
        { id: 'name', type: 'text', label: 'Name', placeholder: 'Enter your name', required: true },
        { id: 'email', type: 'email', label: 'Email', placeholder: 'Enter your email', required: true }
      ],
      submit_text: 'Submit',
      redirect_url: '/thank-you'
    },
    features: {
      title: 'Why Choose Us?',
      features: [
        { icon: 'star', title: 'Feature 1', description: 'Description of feature 1' },
        { icon: 'star', title: 'Feature 2', description: 'Description of feature 2' },
        { icon: 'star', title: 'Feature 3', description: 'Description of feature 3' }
      ]
    },
    testimonial: {
      testimonials: [
        { name: 'Happy Customer', company: 'Company Inc', text: 'This product changed my business!' }
      ]
    },
    cta: {
      headline: 'Ready to Get Started?',
      subheadline: 'Join thousands of successful marketers today',
      button_text: 'Start Now',
      action_type: 'scroll',
      scroll_target: '#form',
      button_url: '#form'
    },
    countdown: {
      title: 'Offer Ends In',
      target_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    },
    pricing: {
      plans: [
        { name: 'Basic', price: 9, features: ['Feature 1', 'Feature 2'] },
        { name: 'Pro', price: 29, features: ['All Basic', 'Feature 3', 'Feature 4'], highlighted: true }
      ]
    },
    checkout: {
      product_name: 'Your Product',
      product_description: 'A brief description of what the customer is buying',
      price: 999,
      currency: 'INR',
      button_text: 'Buy Now',
      success_redirect: '/thank-you',
      show_testimonial: true,
      testimonial: { name: 'Happy Customer', text: 'This product changed my life!' },
      features: ['Feature 1', 'Feature 2', 'Feature 3'],
      guarantee_text: '30-day money-back guarantee'
    }
  };

  return {
    id: `${type}-${Date.now()}`,
    type,
    order: 0,
    props: defaults[type] || {}
  };
}

function renderComponentPreview(component, theme) {
  const props = component.props || {};
  const primaryColor = theme?.primary_color || '#7c3aed';
  const textColor = theme?.text_color || '#f8fafc';

  switch (component.type) {
    case 'hero':
      return (
        <div className="text-center py-8">
          {props.badge_text && (
            <span className="inline-block px-4 py-1 rounded-full text-sm mb-4" style={{ backgroundColor: `${primaryColor}30`, color: primaryColor }}>
              {props.badge_text}
            </span>
          )}
          <h1 className="text-3xl font-bold mb-4" style={{ color: textColor }}>{props.headline || 'Your Headline'}</h1>
          <p className="text-lg opacity-80 mb-6 max-w-xl mx-auto" style={{ color: textColor }}>{props.subheadline || 'Your subheadline'}</p>
          {props.cta_text && (
            <button className="px-6 py-3 rounded-lg font-medium" style={{ backgroundColor: primaryColor, color: '#fff' }}>
              {props.cta_text}
            </button>
          )}
        </div>
      );

    case 'text':
      return (
        <div className="py-6">
          {props.heading && <h2 className="text-2xl font-bold mb-4" style={{ color: textColor }}>{props.heading}</h2>}
          <p style={{ color: textColor }} className="opacity-80">{props.content || 'Text content...'}</p>
        </div>
      );

    case 'form':
      return (
        <div className="max-w-md mx-auto py-6">
          <div className="p-6 rounded-xl" style={{ backgroundColor: `${primaryColor}10`, border: `1px solid ${primaryColor}30` }}>
            <h3 className="text-xl font-semibold mb-4 text-center" style={{ color: textColor }}>{props.title || 'Form Title'}</h3>
            <div className="space-y-3">
              {(props.fields || []).map(field => (
                <div key={field.id}>
                  <label className="block text-sm mb-1 opacity-70" style={{ color: textColor }}>{field.label}</label>
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: textColor }}
                    disabled
                  />
                </div>
              ))}
              <button className="w-full py-3 rounded-lg font-medium mt-4" style={{ backgroundColor: primaryColor, color: '#fff' }}>
                {props.submit_text || 'Submit'}
              </button>
            </div>
          </div>
        </div>
      );

    case 'features':
      return (
        <div className="py-8">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ color: textColor }}>{props.title || 'Features'}</h2>
          <div className="grid grid-cols-3 gap-4">
            {(props.features || []).map((feature, i) => (
              <div key={i} className="p-4 rounded-lg text-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <div className="text-2xl mb-2">✨</div>
                <h4 className="font-medium mb-1" style={{ color: textColor }}>{feature.title}</h4>
                <p className="text-sm opacity-70" style={{ color: textColor }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'testimonial':
      return (
        <div className="py-8">
          <div className="space-y-4">
            {(props.testimonials || []).map((t, i) => (
              <div key={i} className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <p className="italic mb-2" style={{ color: textColor }}>"{t.text}"</p>
                <p className="font-medium text-sm" style={{ color: primaryColor }}>{t.name} - {t.company}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="py-8 text-center" style={{ backgroundColor: `${primaryColor}10` }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: textColor }}>{props.headline || 'Call to Action'}</h2>
          <button className="px-6 py-3 rounded-lg font-medium" style={{ backgroundColor: primaryColor, color: '#fff' }}>
            {props.button_text || 'Click Here'}
          </button>
        </div>
      );

    case 'countdown':
      return (
        <div className="py-8 text-center">
          <h3 className="text-lg mb-4" style={{ color: textColor }}>{props.title || 'Countdown'}</h3>
          <div className="text-3xl font-bold" style={{ color: primaryColor }}>00d 00h 00m 00s</div>
        </div>
      );

    case 'image':
      return (
        <div className="py-4">
          {props.url ? (
            <img src={props.url} alt={props.alt || ''} className="max-w-full rounded-lg mx-auto" />
          ) : (
            <div className="h-48 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <ImageIcon className="w-12 h-12 opacity-50" style={{ color: textColor }} />
            </div>
          )}
        </div>
      );

    case 'video':
      return (
        <div className="py-4">
          <div className="aspect-video rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <Video className="w-12 h-12 opacity-50" style={{ color: textColor }} />
            {props.url && <span className="ml-2 text-sm opacity-50" style={{ color: textColor }}>Video: {props.url}</span>}
          </div>
        </div>
      );

    case 'checkout':
      return (
        <div className="py-8">
          <div className="max-w-md mx-auto p-6 rounded-xl" style={{ backgroundColor: `${primaryColor}10`, border: `1px solid ${primaryColor}30` }}>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2" style={{ color: textColor }}>{props.product_name || 'Product Name'}</h3>
              <p className="opacity-80 text-sm mb-4" style={{ color: textColor }}>{props.product_description || 'Product description'}</p>
              <div className="text-4xl font-bold mb-2" style={{ color: primaryColor }}>
                {props.currency === 'USD' ? '$' : '₹'}{((props.price || 0) / 100).toFixed(0)}
              </div>
            </div>
            
            {(props.features || []).length > 0 && (
              <div className="mb-6 space-y-2">
                {props.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
                    <span style={{ color: primaryColor }}>✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            )}
            
            <button className="w-full py-4 rounded-lg font-bold text-lg" style={{ backgroundColor: primaryColor, color: '#fff' }}>
              {props.button_text || 'Buy Now'}
            </button>
            
            {props.guarantee_text && (
              <p className="text-center text-sm opacity-60 mt-4" style={{ color: textColor }}>
                🛡️ {props.guarantee_text}
              </p>
            )}
            
            {props.show_testimonial && props.testimonial && (
              <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <p className="italic text-sm mb-2" style={{ color: textColor }}>"{props.testimonial.text}"</p>
                <p className="text-sm font-medium" style={{ color: primaryColor }}>- {props.testimonial.name}</p>
              </div>
            )}
          </div>
        </div>
      );

    case 'pricing':
      return (
        <div className="py-8">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(props.plans?.length || 1, 3)}, 1fr)` }}>
            {(props.plans || []).map((plan, i) => (
              <div 
                key={i} 
                className="p-6 rounded-xl text-center"
                style={{ 
                  backgroundColor: plan.highlighted ? `${primaryColor}20` : 'rgba(255,255,255,0.05)',
                  border: plan.highlighted ? `2px solid ${primaryColor}` : '1px solid rgba(255,255,255,0.1)'
                }}
              >
                {plan.highlighted && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs mb-3" style={{ backgroundColor: primaryColor, color: '#fff' }}>
                    POPULAR
                  </span>
                )}
                <h4 className="text-lg font-semibold mb-2" style={{ color: textColor }}>{plan.name}</h4>
                <div className="text-3xl font-bold mb-4" style={{ color: primaryColor }}>
                  ₹{plan.price}
                  <span className="text-sm font-normal opacity-60">/mo</span>
                </div>
                <div className="space-y-2 mb-6 text-sm">
                  {(plan.features || []).map((feature, j) => (
                    <div key={j} style={{ color: textColor }} className="opacity-80">✓ {feature}</div>
                  ))}
                </div>
                <button 
                  className="w-full py-2 rounded-lg font-medium"
                  style={{ 
                    backgroundColor: plan.highlighted ? primaryColor : 'transparent',
                    color: plan.highlighted ? '#fff' : primaryColor,
                    border: `1px solid ${primaryColor}`
                  }}
                >
                  Choose Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return (
        <div className="py-8 text-center opacity-50" style={{ color: textColor }}>
          {component.type} component preview
        </div>
      );
  }
}
